import { useEffect, useState } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';
import { SSExperiments, SSParams } from '@portal/state/context/StatsigContext/StatsigFlags';
import type { NbaOfferPopup, SanityKeyed } from 'types/studio/studio';
import { sanityFetch } from 'sanity-client';
import { LocaleEnum } from 'schema/locale';
import { useLocale } from 'shared/hooks/useLocale';
import { nbaOfferPopup } from 'shared/utils/groq/nbaOfferPopup';
import { useIsLoggedInValue } from 'appLayer/auth';
import type { CommonSectionProps } from '../../types';

type NbaOfferPopupProps = SanityKeyed<NbaOfferPopup> & CommonSectionProps;

const loyaltyOfferMap: Record<string, string> = {
  NEW: 'NBA_15ct_offer',
  'M1-M3': 'NBA_M1-M3_offer',
  'M3-M6': 'NBA_M3-M6_offer',
  'M6-M12': 'NBA_M6-M12_offer',
  'M12+': 'NBA_M12+_offer'
};

export const useNbaOffer = () => {
  const [nbaOffer, setNbaOffer] = useState<NbaOfferPopupProps | null>(null);
  const isLoggedIn = useIsLoggedInValue();
  const { client: statsigClient } = useStatsigClient();
  const locale = useLocale();
  // We only want to show NbaOfferPopup in EU/UK
  const isEuLocale = locale === LocaleEnum.enEU;
  const isUkLocale = locale === LocaleEnum.enUK;
  const language = isEuLocale ? LocaleEnum.enEU : isUkLocale ? LocaleEnum.enUK : null;

  useEffect(() => {
    if (!language) return;

    const fetchNbaOffer = async () => {
      try {
        const offers = (await sanityFetch(nbaOfferPopup, { language })) as
          | NbaOfferPopupProps[]
          | null;
        if (!offers?.length) return;

        let eligibleOffer: NbaOfferPopupProps | null = null;

        if (!isLoggedIn) {
          // Non-members logic

          // Statig experiment for non-members
          const experiment = statsigClient.getExperiment(SSExperiments.nbaAcquisitionExperiment);
          const variant = experiment.get(
            SSParams[SSExperiments.nbaAcquisitionExperiment].offerType
          );

          eligibleOffer =
            offers.find((o) => !o.isMembersOnlyPopup && o.nonMembersOfferType === variant) ?? null;
        } else {
          // Members logic

          // Dummy data. API is in progress for retention side.
          const loyalty = { tier: 'NEW' };
          const expectedOfferType = loyaltyOfferMap[loyalty.tier];

          if (expectedOfferType)
            eligibleOffer =
              offers.find(
                (o) => o.isMembersOnlyPopup && o.membersOfferType === expectedOfferType
              ) ?? null;
        }

        setNbaOffer(eligibleOffer);
      } catch (error) {
        console.error('Failed to fetch nbaOfferPopup:', error);
      }
    };

    fetchNbaOffer();
  }, [language, isLoggedIn, statsigClient]);

  return { nbaOffer };
};
